import { Component, Input } from '@angular/core';
import { isArray } from 'lodash-es';
import { BehaviorSubject } from 'rxjs';
import { arr_filter, data_get, data_has, fallback, filled, OptionalValue } from '../../utils/common';

@Component({
  selector: 'fieldmagic-text',
  template: `
    <ng-container *ngIf="alignment === 'left'; else centerOrRight">
      <ng-container *ngTemplateOutlet="contentElem"></ng-container>
    </ng-container>
    <ng-template #centerOrRight>
      <div class="d-flex justify-content-{{ alignment }}">
        <ng-container *ngTemplateOutlet="contentElem"></ng-container>
      </div>
    </ng-template>
    <ng-template #contentElem>
      <ng-container *ngIf="content$ | async as content">
        <span
          *ngIf="content | filled"
          [matTooltip]="tooltip"
          [class.fieldmagic-text-title]="purpose == 'title'"
          [class.fieldmagic-text-link]="purpose == 'link'"
          [class.fieldmagic-text-input-label]="purpose == 'input'"
          [class.fieldmagic-text-with-required-mark]="withRequiredMarker"
          [class.fieldmagic-text-error]="purpose == 'error'"
          [class.fieldmagic-text-body]="purpose == 'body'"
          [class.fieldmagic-text-help]="purpose == 'help'"
          [class.fieldmagic-text-secondary-display]="purpose == 'secondary-display'"
        >
          {{ content | translate }}
        </span>
      </ng-container>
    </ng-template>
  `,
  styleUrls: ['./text.component.scss'],
})
export class TextComponent {
  @Input()
  set content(value: OptionalValue<any>) {
    if (data_has(value, 'text')) {
      value = data_get(value, 'text');
    }

    this.content$.next(fallback(value, {
      to: () => '',
    }));
  }

  @Input() alignment: TextAlignment = 'left';

  @Input() purpose: TextPurpose = 'body';

  @Input() withRequiredMarker: boolean = false;

  @Input() tooltip?: string;

  readonly content$ = new BehaviorSubject<OptionalValue<string>>(null);
}

@Component({
  selector: 'fieldmagic-input-errors',
  template: `
    <div class="fieldmagic-error-text-containers">
      <ng-container *ngFor="let error of (errors$ | async)">
        <fieldmagic-text
          class="fieldmagic-input-error-text"
          [content]="error"
          purpose="error"
        >
        </fieldmagic-text>
      </ng-container>
    </div>
  `,
  styleUrls: [
    './text.component.scss',
  ]
})
export class InputErrorTextComponent {
  @Input()
  set errors(value: string | string[]) {
    if (! isArray(value)) {
      value = [value];
    }

    this.errors$.next(arr_filter(value, {
      predicate: filled,
    }));
  }

  readonly errors$ = new BehaviorSubject<string[]>([]);
}

@Component({
  selector: 'fieldmagic-text-link',
  template: `
    <a
      *ngIf="label | filled"
      [routerLink]="segments"
      [target]="(openNewTab) ? '_blank' : '_self'"
    >
      <div class="d-flex d-flex-gap align-items-center">
        <span>{{ label | translate}}</span>
      </div>
    </a>

    <span *ngIf="label | blank">--</span>
  `,
})
export class LinkTextComponent {
  @Input() segments: string[] = [];
  @Input() label?: string;
  @Input() openNewTab: boolean = false;
}


export type TextAlignment = 'left' | 'center' | 'right';
type TextPurpose = 'title'
  | 'link'
  | 'body'
  | 'input'
  | 'error'
  | 'table-header'
  | 'help'
  | 'secondary-display';

export type FieldmagicTextPurpose = TextPurpose;